/**
 * FILE-OVERVIEW:
 * - 役割: S3上の出稿ファイル取得・一覧・操作を担当。
 * - 主な連携: components/S3Files/*
 * - 本番運用上の注意: バケット/キー規則変更時はXML出稿側と合わせて更新する。
 */

import { list, downloadData } from '@aws-amplify/storage';
import { S3Client, PutObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { fetchAuthSession } from '@aws-amplify/auth';

// S3バケット設定（環境変数から取得、なければデフォルト値を使用）
const BUCKET_NAME = process.env.REACT_APP_S3_BUCKET_NAME || 'schedule-memo-cts2app';
const OUTPUT_BUCKET_NAME = process.env.REACT_APP_S3_OUTPUT_BUCKET_NAME || 'schedule-memo-app2cts';
const REGION = process.env.REACT_APP_AWS_REGION || 'ap-northeast-1';

/**
 * 入力プレフィックス配下のS3オブジェクト一覧を取得し、画面表示向け形式に整形する。
 *
 * @param {string} prefix 取得対象プレフィックス
 * @returns {Promise<Array<{key:string,size:number,lastModified:Date,fileName:string}>>}
 */
export const listFiles = async (prefix = '') => {
  try {
    const result = await list({
      path: prefix,
      options: {
        bucket: {
          bucketName: BUCKET_NAME,
          region: REGION,
        },
        listAll: true,
      },
    });

    if (!result.items || result.items.length === 0) {
      return [];
    }

    // ファイル情報を整形
    const files = result.items.map(item => ({
      key: item.path,
      size: item.size,
      lastModified: item.lastModified,
      fileName: item.path.split('/').pop(), // ファイル名のみ
    }));

    return files;
  } catch (error) {
    console.error('ファイル一覧取得エラー:', error);
    throw error;
  }
};

/**
 * S3上の指定ファイルをブラウザ経由でダウンロードする。
 *
 * @param {string} fileKey S3キー
 * @returns {Promise<void>}
 *
 * 副作用:
 * - URL.createObjectURL と一時 a 要素を使用してクライアント保存を実行する。
 */
export const downloadFile = async (fileKey) => {
  try {
    const downloadResult = await downloadData({
      path: fileKey,
      options: {
        bucket: {
          bucketName: BUCKET_NAME,
          region: REGION,
        },
      },
    }).result;

    // レスポンスボディをBlobに変換
    const blob = await downloadResult.body.blob();

    // ダウンロードリンクを作成
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileKey.split('/').pop(); // ファイル名のみ
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('ファイルダウンロードエラー:', error);
    throw error;
  }
};

/**
 * バイト数を人間可読形式へ変換する。
 *
 * @param {number} bytes バイト数
 * @returns {string} 例: 1.5 MB
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * S3上のXMLファイルを文字列で取得する。
 *
 * @param {string} fileKey S3キー
 * @returns {Promise<string>} XML文字列
 */
export const getXmlContent = async (fileKey) => {
  try {
    const downloadResult = await downloadData({
      path: fileKey,
      options: {
        bucket: {
          bucketName: BUCKET_NAME,
          region: REGION,
        },
      },
    }).result;

    // レスポンスボディをテキストに変換
    const bodyContents = await downloadResult.body.text();

    return bodyContents;
  } catch (error) {
    console.error('XMLファイル取得エラー:', error);
    throw error;
  }
};

/**
 * XMLから ThemeInfo/ContentsInfo を抽出し、画面処理向けの素材配列へ正規化する。
 *
 * @param {string} xmlString 入力XML
 * @returns {Array<Object>} 素材情報配列
 *
 * 本番運用上の注意:
 * - 連携元XMLタグ名変更時は selector 一式の更新が必要。
 */
export const parseXmlContents = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // パースエラーチェック
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XMLのパースに失敗しました');
    }

    const contents = [];

    // ThemeInfo要素を取得
    const themeInfos = xmlDoc.getElementsByTagName('ThemeInfo');

    Array.from(themeInfos).forEach((themeInfo) => {
      const themeName = themeInfo.querySelector('ThemeName')?.textContent || '';
      const themeComment = themeInfo.querySelector('ThemeComment')?.textContent || '';
      const newsGenre = themeInfo.querySelector('NewsGenre')?.textContent || '';
      const priority = themeInfo.querySelector('Priority')?.textContent || '';
      const memoId = themeInfo.querySelector('MemoID')?.textContent || '';
      const lastModifiedDate = themeInfo.querySelector('LastModifiedDate')?.textContent || '';
      const initialRegistrationDate = themeInfo.querySelector('InitialRegistrationDate')?.textContent || '';

      // ContentsInfo要素を取得
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');

      // テーマレベルの日付情報
      const firstCreatedDate = themeInfo.querySelector('FirstCreatedDate')?.textContent || '';

      Array.from(contentsInfos).forEach((contentsInfo) => {
        // ContentsInfo内のFirstCreatedDateを優先、なければThemeInfoのものを使用
        const contentFirstCreatedDate = contentsInfo.querySelector('FirstCreatedDate')?.textContent || firstCreatedDate;

        const content = {
          // テーマ情報
          themeName,
          themeComment,
          newsGenre,
          priority,
          memoId,
          firstCreatedDate: contentFirstCreatedDate,
          lastModifiedDate,
          initialRegistrationDate,
          // 素材情報
          memoKind: contentsInfo.querySelector('MemoKind')?.textContent || '',
          version: contentsInfo.querySelector('Version')?.textContent || '',
          childMemoId: contentsInfo.querySelector('ChildMemoID')?.textContent || '',
          headLine: contentsInfo.querySelector('HeadLine')?.textContent || '',
          editInfo: contentsInfo.querySelector('EditInfo')?.textContent || '',
          registrationDate: contentsInfo.querySelector('RegistrationDate')?.textContent || '',
          subjectCode1: contentsInfo.querySelector('SubjectCode1')?.textContent || '',
          linkUrl: contentsInfo.querySelector('LinkURL')?.textContent || '',
          mediaStatus: (contentsInfo.querySelector('MediaStatus')?.textContent || '').replace(/\s/g, ''),
          latestSendDate: contentsInfo.querySelector('LatestSendDate')?.textContent || '',
          publishingStatus: (contentsInfo.querySelector('PublishingStatus')?.textContent || '').replace(/\s/g, ''),
        };

        // 掲載情報を取得
        const publishingInfos = contentsInfo.getElementsByTagName('PublishingInfo');
        content.publishingInfoList = Array.from(publishingInfos).map((pubInfo) => {
          // Year/Month/Day形式を優先、なければPublishingDateを使用
          const pubYear = pubInfo.querySelector('PublishingYear')?.textContent || '';
          const pubMonth = pubInfo.querySelector('PublishingMonth')?.textContent || '';
          const pubDay = pubInfo.querySelector('PublishingDay')?.textContent || '';
          const pageName = pubInfo.querySelector('PageName')?.textContent || '';
          const publishingDate = pubInfo.querySelector('PublishingDate')?.textContent || '';
          const publishingMedia = pubInfo.querySelector('PublishingMedia')?.textContent || '';
          const publishingPage = pubInfo.querySelector('PublishingPage')?.textContent || pageName;

          let date = publishingDate;
          if (pubYear && pubMonth && pubDay) {
            date = `${pubYear}/${pubMonth.padStart(2, '0')}/${pubDay.padStart(2, '0')}`;
          }

          return {
            publishingDate: date,
            publishingMedia,
            publishingPage,
          };
        });

        contents.push(content);
      });
    });

    return contents;
  } catch (error) {
    console.error('XMLパースエラー:', error);
    throw error;
  }
};

/**
 * 当日分の reporter_memo ファイルを取得し、日付キー単位でグルーピングする。
 *
 * @returns {Promise<Record<string, Array>>} { YYYY-MM-DD: FileInfo[] }
 */
export const listReporterMemoFiles = async () => {
  try {
    // 当日の日付を取得（YYYYMMDD形式）
    const today = new Date();
    const todayStr = today.getFullYear().toString() +
      (today.getMonth() + 1).toString().padStart(2, '0') +
      today.getDate().toString().padStart(2, '0');

    // まずプレフィックスなしで全ファイルを取得してみる
    const allFiles = await listFiles('');

    // reporter_memo/プレフィックス付きでも取得してみる
    const filesWithPrefix = await listFiles('reporter_memo/');

    // どちらかで取得できたファイルを使用
    const files = filesWithPrefix.length > 0 ? filesWithPrefix : allFiles;

    // ファイル名から日付を抽出してマップを作成（当日のファイルのみ）
    const filesByDate = {};
    files.forEach(file => {
      // ファイル名から日付部分を抽出（最初の_と2つ目の_の間のYYYYMMDD）
      // 例: 1DATE_20251024_1(メモのみ).xml → 20251024
      const match = file.fileName.match(/_(\d{8})_/);
      if (match) {
        const dateStr = match[1]; // 例: "20251024"

        // 当日のファイルのみを対象とする
        if (dateStr !== todayStr) {
          return;
        }

        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const dateKey = `${year}-${month}-${day}`; // ISO形式: "2025-10-24"

        // 同じ日付に複数のファイルがある場合は配列で保持
        if (!filesByDate[dateKey]) {
          filesByDate[dateKey] = [];
        }
        filesByDate[dateKey].push(file);
      }
    });

    return filesByDate;
  } catch (error) {
    console.error('reporter_memoファイル取得エラー:', error);
    throw error;
  }
};

/**
 * 当日XML群を読み込み、素材情報を横断収集して返す。
 *
 * @returns {Promise<Array<Object>>} 素材情報配列
 *
 * 補足:
 * - InitialRegistrationDate を優先して dateKey を決定し、なければ作成日時・ファイル日付へフォールバックする。
 */
export const listTodayContents = async () => {
  try {
    // 当日のファイル一覧を取得
    const filesByDate = await listReporterMemoFiles();

    const allContents = [];

    // 各ファイルのXMLを取得してパース
    for (const fileDateKey of Object.keys(filesByDate)) {
      const files = filesByDate[fileDateKey];
      for (const file of files) {
        try {
          const xmlContent = await getXmlContent(file.key);
          const contents = parseXmlContents(xmlContent);
          // 各素材情報に日付とファイル情報を追加
          contents.forEach(content => {
            // InitialRegistrationDate（登録予定日）からカレンダー表示用の日付を生成
            // フォールバック: firstCreatedDate → ファイルの日付
            // 形式: "2026/01/10 02:50:00" → "2026-01-10"
            let dateKey = fileDateKey; // デフォルトはファイルの日付
            if (content.initialRegistrationDate) {
              const match = content.initialRegistrationDate.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
              if (match) {
                dateKey = `${match[1]}-${match[2]}-${match[3]}`;
              }
            } else if (content.firstCreatedDate) {
              const match = content.firstCreatedDate.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
              if (match) {
                dateKey = `${match[1]}-${match[2]}-${match[3]}`;
              }
            }
            allContents.push({
              ...content,
              dateKey,
              sourceFile: file,
            });
          });
        } catch (err) {
          console.error(`XMLパースエラー (${file.key}):`, err);
        }
      }
    }

    return allContents;
  } catch (error) {
    console.error('素材情報一覧取得エラー:', error);
    throw error;
  }
};

/**
 * 素材配列をテーマ単位へ集約する。
 *
 * @param {Array<Object>} contents 素材情報配列
 * @returns {Array<Object>} テーマ単位の集約配列
 */
export const groupContentsByTheme = (contents) => {
  const themeMap = {};

  contents.forEach((content) => {
    const key = content.memoId || content.themeName || 'unknown';
    if (!themeMap[key]) {
      themeMap[key] = {
        themeName: content.themeName,
        themeComment: content.themeComment,
        newsGenre: content.newsGenre,
        priority: content.priority,
        memoId: content.memoId,
        firstCreatedDate: content.firstCreatedDate,
        lastModifiedDate: content.lastModifiedDate,
        initialRegistrationDate: content.initialRegistrationDate,
        dateKey: content.dateKey,
        sourceFile: content.sourceFile,
        contents: [],
      };
    }
    themeMap[key].contents.push(content);
  });

  return Object.values(themeMap);
};

/**
 * XML文字列を閲覧用HTMLへ変換する。
 *
 * @param {string} xmlString 入力XML
 * @returns {string} クラス付きHTML文字列
 */
export const convertXmlToHtml = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // パースエラーチェック
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XMLのパースに失敗しました');
    }

    const root = xmlDoc.documentElement;

    // XMLをHTMLに変換（要素ごとにクラス付与）
    return `<div class="xml-content">${elementToHtml(root)}</div>`;
  } catch (error) {
    console.error('XML→HTML変換エラー:', error);
    throw error;
  }
};

// 日本語ラベルマッピング
const tagLabels = {
  XmlInfo: 'XML情報',
  SubmitDate: '実行日時',
  SearchDateType: '検索日付種別',
  SortDateType: 'ソート日付種別',
  SortOrder: 'ソート順',
  SearchStartDate: '出力開始期間',
  SearchEndDate: '出力終了期間',
  OutputThemeCount: '出力テーマ件数',
  ThemeInfoList: 'テーマ情報一覧',
  ThemeInfo: 'テーマ情報',
  ThemeName: 'テーマ名',
  ThemeComment: 'テーマコメント',
  NewsGenre: 'ニュースジャンル',
  Priority: '優先度',
  MemoID: 'メモID',
  InitialRegistrationDate: '初期登録日',
  FirstCreatedDate: '作成日時',
  LastModifiedDate: '最終更新日時',
  TextInfoCnt: 'テキスト情報数',
  PhotoInfoCnt: '写真情報数',
  ContentsInfoList: '素材情報一覧',
  ContentsInfo: '素材情報',
  MemoKind: 'メモ種別',
  Version: 'バージョン',
  ChildMemoID: '子メモID',
  HeadLine: '見出し',
  EditInfo: '編集情報',
  RegistrationDate: '登録日',
  SubjectCode1: 'サブジェクトコード1',
  LinkURL: 'リンクURL',
  MediaStatus: 'メディア状態',
  LatestSendDate: '最終送信日',
  PublishingStatus: '掲載状態',
  PublishingInfoList: '掲載情報一覧',
  PublishingInfo: '掲載情報',
  PublishingDate: '掲載日',
  PublishingMedia: '掲載メディア',
  PublishingPage: '掲載ページ',
};

// 要素カテゴリ分類
const elementCategories = {
  header: ['SubmitDate', 'SearchDateType', 'SortDateType', 'SortOrder', 'SearchStartDate', 'SearchEndDate', 'OutputThemeCount'],
  theme: ['ThemeName', 'ThemeComment', 'NewsGenre', 'Priority', 'MemoID'],
  date: ['InitialRegistrationDate', 'FirstCreatedDate', 'LastModifiedDate', 'RegistrationDate', 'LatestSendDate', 'PublishingDate'],
  count: ['TextInfoCnt', 'PhotoInfoCnt'],
  contents: ['MemoKind', 'Version', 'ChildMemoID', 'HeadLine', 'EditInfo', 'SubjectCode1', 'LinkURL'],
  status: ['MediaStatus', 'PublishingStatus'],
  list: ['ThemeInfoList', 'ContentsInfoList', 'PublishingInfoList'],
  item: ['ThemeInfo', 'ContentsInfo', 'PublishingInfo'],
};

// カテゴリ取得
const getCategory = (tagName) => {
  for (const [category, tags] of Object.entries(elementCategories)) {
    if (tags.includes(tagName)) return category;
  }
  return 'default';
};

/**
 * XML要素を再帰的にHTMLに変換（要素ごとにクラス付与）
 */
const elementToHtml = (element) => {
  const tagName = element.nodeName;
  const label = tagLabels[tagName] || tagName;
  const category = getCategory(tagName);
  const children = Array.from(element.childNodes);

  // 子ノードがテキストのみの場合
  if (children.length === 1 && children[0].nodeType === 3) {
    const text = children[0].textContent.trim();
    if (text) {
      return `<div class="xml-element xml-${tagName} xml-category-${category}">
        <span class="xml-label">${label}:</span>
        <span class="xml-value">${text}</span>
      </div>`;
    }
    return '';
  }

  // 子要素がある場合
  if (element.children.length > 0) {
    let html = `<div class="xml-element xml-${tagName} xml-category-${category}">
      <div class="xml-label xml-section-header">${label}</div>
      <div class="xml-children">`;
    Array.from(element.children).forEach(child => {
      html += elementToHtml(child);
    });
    html += '</div></div>';
    return html;
  }

  return '';
};

/**
 * 文字列→UTF-16BE(BOM付き)のUint8Arrayへ変換
 * @param {string} str - 変換する文字列
 * @returns {Uint8Array} UTF-16BEバイト配列
 */
const toUtf16BEUint8Array = (str) => {
  const buf = new Uint8Array((str.length + 1) * 2);
  buf[0] = 0xFE; buf[1] = 0xFF;
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    buf[2 + i * 2] = (code >> 8) & 0xFF;
    buf[3 + i * 2] = code & 0xFF;
  }
  return buf;
};

/**
 * 生成済みメモXMLを出力バケットへアップロードする。
 *
 * @param {string} xmlString XML文字列
 * @param {string} s3Key 出力先キー（例: article/memo/xxxx.xml）
 * @returns {Promise<string>} 保存先S3キー
 *
 * 仕様:
 * - 本関数は UTF-16BE(BOM付き) に変換して保存する。
 */
export const uploadMemoXml = async (xmlString, s3Key) => {
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
  });

  const xmlBytes = toUtf16BEUint8Array(xmlString);

  const command = new PutObjectCommand({
    Bucket: OUTPUT_BUCKET_NAME,
    Key: s3Key,
    Body: xmlBytes,
    ContentType: 'application/xml;charset=UTF-16BE',
  });

  await s3Client.send(command);
  return s3Key;
};

/**
 * 既存メモXMLを部分更新し、同一キーへ上書き保存する。
 *
 * @param {string} fileKey 対象XMLのS3キー
 * @param {Object} updates 更新差分
 * @returns {Promise<string>} 更新後S3キー
 *
 * 本番運用上の注意:
 * - 元ファイルの文字コード（UTF-16BE/UTF-8）を維持して再保存する。
 * - MemoID/ChildMemoID で対象要素を特定するため、ID運用ルールの変更時は要見直し。
 */
export const updateExistingMemoXml = async (fileKey, updates) => {
  // 1. 既存XMLをバイト列でダウンロード（エンコーディング保持のため）
  const session = await fetchAuthSession();
  const s3Client = new S3Client({
    region: REGION,
    credentials: session.credentials,
  });

  const getResult = await s3Client.send(new GetObjectCommand({
    Bucket: BUCKET_NAME,
    Key: fileKey,
  }));
  const rawBytes = new Uint8Array(await getResult.Body.transformToByteArray());

  // 2. エンコーディング検出・デコード
  let xmlString;
  let isUtf16BE = false;
  if (rawBytes.length >= 2 && rawBytes[0] === 0xFE && rawBytes[1] === 0xFF) {
    isUtf16BE = true;
    xmlString = new TextDecoder('utf-16be').decode(rawBytes);
  } else {
    xmlString = new TextDecoder('utf-8').decode(rawBytes);
  }

  // 3. 元のXML宣言を保持（XMLSerializerが書き換えるため復元用）
  const xmlDeclMatch = xmlString.match(/^<\?xml[^?]*\?>/);
  const xmlDeclaration = xmlDeclMatch ? xmlDeclMatch[0] : '';

  // 4. DOMパース
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const parserError = xmlDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('既存XMLのパースに失敗しました');
  }

  // 5. ThemeInfo要素を更新（MemoIDで照合）
  const themeInfos = xmlDoc.getElementsByTagName('ThemeInfo');

  Array.from(themeInfos).forEach((themeInfo) => {
    const memoId = themeInfo.querySelector('MemoID')?.textContent || '';
    if (updates.memoId && memoId !== updates.memoId) {
      return;
    }

    const setField = (selector, value) => {
      if (value === undefined) return;
      const el = themeInfo.querySelector(selector);
      if (!el) return;
      const before = el.textContent;
      // 新しい値が空で元に値がある場合は上書きしない
      if (!value && before) return;
      if (before !== value) {
        el.textContent = value;
      }
    };

    setField('ThemeName', updates.themeName);
    setField('ThemeComment', updates.themeComment);
    setField('NewsGenre', updates.newsGenre);
    setField('Priority', updates.priority);
    setField('InitialRegistrationDate', updates.registrationDate);

    // 6. ContentsInfo要素を更新（ChildMemoIDで照合）
    if (updates.materials) {
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');

      Array.from(contentsInfos).forEach((ci) => {
        const childId = ci.querySelector('ChildMemoID')?.textContent || '';
        const mat = updates.materials.find(m => m.childMemoId === childId);
        if (!mat) return;

        const setCiField = (sel, val) => {
          if (val === undefined) return;
          const el = ci.querySelector(sel);
          if (!el) return;
          const before = el.textContent;
          // 新しい値が空で元に値がある場合は上書きしない
          if (!val && before) return;
          // RegistrationDate: 日付部分が同じなら上書きしない（フォーマット保護）
          if (sel === 'RegistrationDate' && before && val) {
            const beforeDate = before.replace(/[\/ \-:]/g, '').substring(0, 8);
            const valDate = val.replace(/[\/ \-:]/g, '').substring(0, 8);
            if (beforeDate === valDate) return;
          }
          if (before !== val) {
            el.textContent = val;
          }
        };

        setCiField('MemoKind', mat.memoKind);
        setCiField('HeadLine', mat.headline);
        setCiField('EditInfo', mat.editInfo);
        setCiField('LinkURL', mat.linkUrl);
        setCiField('SubjectCode1', updates.category);
        setCiField('RegistrationDate', mat.registrationDate);
      });
    }

    // 6b. 素材削除: 該当ContentsInfo要素をDOMから除去
    if (updates.deletedChildMemoIds && updates.deletedChildMemoIds.length > 0) {
      const contentsInfos = themeInfo.getElementsByTagName('ContentsInfo');
      // 逆順で削除（インデックスずれ防止）
      const ciArray = Array.from(contentsInfos);
      for (let i = ciArray.length - 1; i >= 0; i--) {
        const childId = ciArray[i].querySelector('ChildMemoID')?.textContent || '';
        if (updates.deletedChildMemoIds.includes(childId)) {
          ciArray[i].parentNode.removeChild(ciArray[i]);
        }
      }
    }

    // 6c. 素材追加: 新しいContentsInfo要素を追加
    if (updates.newMaterials && updates.newMaterials.length > 0) {
      // ContentsInfoListを探す（なければThemeInfo直下に追加）
      let contentsInfoList = themeInfo.querySelector('ContentsInfoList');
      if (!contentsInfoList) {
        contentsInfoList = xmlDoc.createElement('ContentsInfoList');
        themeInfo.appendChild(contentsInfoList);
      }

      updates.newMaterials.forEach((mat) => {
        const ci = xmlDoc.createElement('ContentsInfo');
        const fields = [
          ['MemoKind', mat.memoKind || ''],
          ['Version', '1'],
          ['ChildMemoID', mat.childMemoId || ''],
          ['HeadLine', mat.headline || ''],
          ['EditInfo', mat.editInfo || ''],
          ['RegistrationDate', mat.registrationDate || ''],
          ['SubjectCode1', updates.category || ''],
          ['LinkURL', mat.linkUrl || ''],
          ['MediaStatus', ''],
          ['LatestSendDate', ''],
          ['PublishingStatus', '未掲載'],
        ];
        fields.forEach(([tag, val]) => {
          const el = xmlDoc.createElement(tag);
          el.textContent = val;
          ci.appendChild(el);
        });
        contentsInfoList.appendChild(ci);
      });
    }

    // 6d. TextInfoCnt / PhotoInfoCnt を再計算
    const allCi = themeInfo.getElementsByTagName('ContentsInfo');
    let textCnt = 0;
    let photoCnt = 0;
    Array.from(allCi).forEach((ci) => {
      const kind = ci.querySelector('MemoKind')?.textContent || '';
      if (kind === '記事' || kind === '1') textCnt++;
      else photoCnt++;
    });
    const textCntEl = themeInfo.querySelector('TextInfoCnt');
    if (textCntEl) textCntEl.textContent = String(textCnt);
    const photoCntEl = themeInfo.querySelector('PhotoInfoCnt');
    if (photoCntEl) photoCntEl.textContent = String(photoCnt);
  });

  // 7. XMLシリアライズ（元のXML宣言を復元）
  const serializer = new XMLSerializer();
  let updatedXml = serializer.serializeToString(xmlDoc);
  updatedXml = updatedXml.replace(/^<\?xml[^?]*\?>/, '');
  if (xmlDeclaration) {
    updatedXml = xmlDeclaration + updatedXml;
  }

  // 8. 元と同じエンコーディングでアップロード（同一キーで上書き）
  let uploadBytes;
  if (isUtf16BE) {
    uploadBytes = toUtf16BEUint8Array(updatedXml);
  } else {
    uploadBytes = new TextEncoder().encode(updatedXml);
  }

  const putCommand = new PutObjectCommand({
    Bucket: BUCKET_NAME,
    Key: fileKey,
    Body: uploadBytes,
    ContentType: isUtf16BE ? 'application/xml;charset=UTF-16BE' : 'application/xml;charset=UTF-8',
  });

  await s3Client.send(putCommand);
  return fileKey;
};
